import React, { Fragment, useEffect, useState } from 'react';
import {
  batch, shallowEqual, useDispatch, useSelector
} from 'react-redux';
import { DateTime } from 'luxon';
import { useSnackbar } from 'notistack';

import Layout from '../../../layout/Layout';
import { permissions } from '../../../common/permissionGroup';
import AccessDenied from '../../../components/AccessDenied/AccessDenied';
import PermissionsGate from '../../../components/PermissionsGate/PermissionsGate';
import Text from '../../../components/Text/Text';
import Card from '../../../components/Card/Card';
import Table from '../../../components/Table/Table';
import TableHead from '../../../components/Table/TableHead';
import TableRow from '../../../components/Table/TableRow';
import TableHeadData from '../../../components/Table/TableHeadData';
import TableBody from '../../../components/Table/TableBody';
import Pagination from '../../../components/Pagination/Pagination';
import { coOrderedPartsTable } from '../../../constants/coOrderedPartsTable';
import EmptyTable from '../../../components/Table/EmptyTable';
import getOrderedParts from '../../../thunks/coilovers/orderedParts/getOrderedParts';
import { setOrderedPartsFilter } from '../../../actions/coilovers/orderedParts/orderedPartsFilter';
import TableData from '../../../components/Table/TableData';
import ButtonTypes from '../../../constants/buttonTypes';
import Badge from '../../../components/Badge/Badge';
import SearchInput from '../../../components/SearchInput/SearchInput';
import OrderedPartsRightHeader from './components/OrderedPartsRightHeader';
import OrderedPartsActions from './components/OrderedPartsActions';
import OrderedPartsEditModal from './components/OrderedPartsEditModal';
import ConfirmModal from '../../../components/ConfirmModal/ConfirmModal';
import deleteOrderedParts from '../../../thunks/coilovers/orderedParts/deleteOrderedParts';
import {
  clearDeleteOrderedParts, clearPatchOrderedParts,
} from '../../../actions/coilovers/orderedParts/patchOrderedParts';
import { IconAlertCircle, IconArrowDown, IconCross } from '../../../assets/icon';
import patchOrderedParts from '../../../thunks/coilovers/orderedParts/patchOrderedParts';
import orderedPartsStatus, { orderStatusInfo } from '../../../constants/orderedPartsStatus';
import prepareProviderLink from '../../../helpers/general';
import { setApplicationState } from '../../../actions/application/application';
import OrderedPartsSubheader from './components/OrderedPartsSubheader';
import toggleArrayItem from '../../../helpers/toggleArrayItem';
import useQueryParams from '../../../hooks/useQueryParams';
import DeliveredOrderedPartsModal from '../../../components/DeliveredOrderedPartsModal/DeliveredOrderedPartsModal';
import CorrectionOrderedPartsModal from '../../../components/CorrectionOrderedPartsModal/CorrectionOrderedPartsModal';
import OrderedPartDetailsRow, { getTrackingNumbers } from './components/OrderedPartDetailsRow';

const searchHeaderClassName = 'w-[250px]';
const tableContainerClassName = 'overflow-auto';
const actionCellClassName = 'w-[60px] py-0 pl-0 pr-[9px]';
const tableCellClassName = 'min-w-max whitespace-nowrap px-[9px] py-0 text-center';
const noteClassName = 'max-w-[320px] truncate text-left';
const detailsButtonClassName = `inline-flex h-[30px] w-[30px] items-center justify-center rounded text-primary
  hover:bg-primary/10 focus:outline-none focus:ring-2 focus:ring-primary/30`;
const trackingMoreButtonClassName = `ml-[8px] hidden rounded bg-primary/10 px-[6px] py-[2px] text-[11px]
  font-semibold text-primary hover:bg-primary/20 md:inline-flex`;

const OrderedParts = () => {
  const dispatch = useDispatch();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [timer, setTimer] = useState();
  const [editModalVisibility, setEditModalVisibility] = useState();
  const [deleteModalVisibility, setDeleteModalVisibility] = useState();
  const [archivedModalVisibility, setArchivedModalVisibility] = useState();
  const [deliveredModalVisibility, setDeliveredModalVisibility] = useState();
  const [correctionModalVisibility, setCorrectionModalVisibility] = useState();
  const [editItem, setEditItem] = useState({});
  const [deleteId, setDeleteId] = useState();
  const [archivedId, setArchivedId] = useState();
  const [expandedOrderedPartIds, setExpandedOrderedPartIds] = useState(() => new Set());
  const [queryParams, setQueryParams] = useQueryParams();

  const {
    getOrderedParts: {
      loading, data, currentPage, lastPage
    },
    orderedPartsFilter: {
      query, size, order,
    },
    orderedPartsFilter,
    deleteOrderedParts: { id: deletedId, error: deleteError, loading: deleteLoading },
    patchOrderedParts: { data: patchedData },
    application: { selectedRow, orderedPartsTableSetting }
  } = useSelector((state) => ({
    getOrderedParts: state?.getOrderedParts,
    orderedPartsFilter: state?.orderedPartsFilter,
    deleteOrderedParts: state?.deleteOrderedParts,
    patchOrderedParts: state?.patchOrderedParts,
    application: state?.application
  }), shallowEqual);

  useEffect(() => {
    if (Object.keys(patchedData).length) {
      dispatch(clearPatchOrderedParts());
      setArchivedModalVisibility(false);
    }
  }, [patchedData]);

  useEffect(() => {
    clearTimeout(timer);
    const t = setTimeout(() => batch(() => {
      dispatch(setOrderedPartsFilter({ page: 1 }));
      dispatch(getOrderedParts());
    }), 800);
    setTimer(t);
  }, [query]);

  useEffect(() => {
    setQueryParams({
      ...(query && { query }),
      ...(order && { order }),
      ...(orderedPartsFilter?.partStatus?.length && { partStatus: orderedPartsFilter?.partStatus.join(',') }),
      ...(orderedPartsFilter?.orderedType?.length && { orderedType: orderedPartsFilter?.orderedType.join(',') }),
      ...(orderedPartsFilter?.orderDateFrom && { orderDateFrom: orderedPartsFilter?.orderDateFrom }),
      ...(orderedPartsFilter?.orderDateTo && { orderDateTo: orderedPartsFilter?.orderDateTo }),
    });
  }, [orderedPartsFilter]);

  useEffect(() => {
    if (deletedId) {
      enqueueSnackbar('Part delete successfully', { variant: 'success' });
      dispatch(clearDeleteOrderedParts());
      setDeleteModalVisibility(false);
    }
    if (deleteError) {
      enqueueSnackbar(deleteError, {
        variant: 'error',
        anchorOrigin: {
          vertical: 'top',
          horizontal: 'center',
        },
        persist: true,
        action: (id) => (
          <div role="presentation" onClick={() => closeSnackbar(id)}>
            <IconCross />
          </div>
        )
      });
    }
  }, [deletedId, deleteError]);

  useEffect(() => {
    const filterData = {
      ...(queryParams.query?.length && { query: queryParams.query }),
      ...(queryParams.order?.length && { order: queryParams.order }),
      ...(queryParams.partStatus?.length && { partStatus: queryParams.partStatus.split(',') }),
      ...(queryParams.orderedType?.length && { orderedType: queryParams.orderedType.split(',') }),
      ...(queryParams.orderDateFrom && { orderDateFrom: queryParams.orderDateFrom }),
      ...(queryParams.orderDateTo && { orderDateTo: queryParams.orderDateTo }),
    };

    dispatch(setOrderedPartsFilter(filterData));

    return batch(() => {
      dispatch(clearDeleteOrderedParts());
      dispatch(clearPatchOrderedParts());
    });
  }, []);

  const onPagePress = (page) => batch(() => {
    batch(() => {
      dispatch(setOrderedPartsFilter({ page }));
      dispatch(getOrderedParts());
    });
  });

  const onSizeChange = ({ value }) => {
    batch(() => {
      dispatch(setOrderedPartsFilter({ size: value }));
      dispatch(getOrderedParts());
    });
  };

  const onSort = (isSort, sortStr) => {
    if (isSort) {
      batch(() => {
        dispatch(setOrderedPartsFilter({ order: sortStr }));
        dispatch(getOrderedParts());
      });
    }
  };

  const selectRow = (id) => {
    dispatch(setApplicationState({
      selectedRow: toggleArrayItem(selectedRow, id)
    }));
  };

  const toggleOrderedPartDetails = (id) => {
    setExpandedOrderedPartIds((currentIds) => {
      const nextIds = new Set(currentIds);
      if (nextIds.has(id)) {
        nextIds.delete(id);
      } else {
        nextIds.add(id);
      }
      return nextIds;
    });
  };

  const expandOrderedPartDetails = (id) => {
    setExpandedOrderedPartIds((currentIds) => {
      if (currentIds.has(id)) return currentIds;
      return new Set(currentIds).add(id);
    });
  };

  const getColumn = () => {
    if (orderedPartsTableSetting?.length) {
      return coOrderedPartsTable.filter(({ source }) => orderedPartsTableSetting.includes(source));
    }
    return coOrderedPartsTable;
  };

  return (
    <Layout pageTitle="Ordered parts" subheader={<OrderedPartsSubheader />}>
      <PermissionsGate
        can={permissions.COW_READ}
        errorComponent={<AccessDenied />}
      >
        <Card
          showHeader
          headerRight={<OrderedPartsRightHeader />}
          headerLeft={(
            <div className={searchHeaderClassName}>
              <SearchInput
                isClearable
                value={query}
                onChange={({ target: { value } }) => dispatch(setOrderedPartsFilter({ query: value }))}
              />
            </div>
          )}
          headerBordered={false}
          footerBordered={false}
          footer={(
            <div className="flex justify-end">
              <Pagination
                currentPage={currentPage}
                onPagePress={onPagePress}
                lastPage={lastPage}
                withPaginationSize
                onSizeChange={onSizeChange}
                size={size}
              />
            </div>
          )}
        >
          <div className={tableContainerClassName}>
            <Table loading={loading}>
              <TableHead>
                <TableRow bordered>
                  <TableHeadData />
                  <PermissionsGate can={permissions.COW_UPDATE}>
                    <TableHeadData />
                  </PermissionsGate>
                  {
                    getColumn().map(({ title, source, sortable }) => (
                      <TableHeadData
                        key={source}
                        sortable={sortable}
                        order={order}
                        source={source}
                        onSort={(val) => onSort(sortable, val)}
                      >
                        {title}
                      </TableHeadData>
                    ))
                    }
                </TableRow>
              </TableHead>
              <TableBody>
                {data?.length ? data.map((orderedPart, index) => {
                  const {
                  _id,
                  ordered_part_id: id,
                  ordered_part_order_date: orderDate,
                  ordered_part_delivered_date: deliveredDate,
                  ordered_part_po: po,
                  ordered_part_vendor: vendor,
                  ordered_part_status: partStatus,
                  ordered_type: orderedType,
                  ordered_part_type: partType,
                  ordered_part_brand: brand,
                  ordered_part_mpn: mpn,
                  ordered_part_qty_delivered: qtyDelivered,
                  ordered_part_qty_in_order: qtyInOrder,
                  ordered_part_qty_return: qtyReturn,
                  ordered_part_tracking: tracking,
                  ordered_part_note: note,
                  ordered_part_price_paid: paid,
                  ordered_part_agent: agent,
                  ordered_part_est_delivery_date: estDate,
                  ordered_part_included: include,
                  ordered_part_price_in_order: priceInOrder
                  } = orderedPart;
                  const trackingArr = getTrackingNumbers(tracking);
                  const detailsExpanded = expandedOrderedPartIds.has(_id);
                  return (
                    <Fragment key={`ordered_part_${_id}`}>
                      <TableRow
                        bordered={index < data.length - 1}
                        onClick={() => selectRow(_id)}
                        selected={selectedRow.includes(_id)}
                        dataRow={_id}
                      >
                        <TableData
                          className="orderDetailsControl w-[42px] min-w-[42px] px-[5px] py-0 text-center"
                        >
                          <button
                            type="button"
                            className={detailsButtonClassName}
                            aria-expanded={detailsExpanded}
                            aria-controls={`ordered-part-details-${_id}`}
                            aria-label={`${detailsExpanded ? 'Hide' : 'Show'} details for ordered part ${id}`}
                            onClick={(event) => {
                              event.stopPropagation();
                              toggleOrderedPartDetails(_id);
                            }}
                          >
                            <IconArrowDown
                              width={18}
                              height={18}
                              className={`transition-transform ${detailsExpanded ? 'rotate-180' : ''}`}
                            />
                          </button>
                        </TableData>
                        <PermissionsGate can={permissions.COW_UPDATE}>
                          <TableData className={actionCellClassName}>
                            <OrderedPartsActions
                              side={index < data.length - 3 ? 'right' : 'top'}
                              onEdit={() => {
                              setEditItem(data[index]);
                              setEditModalVisibility(true);
                            }}
                              onDelete={() => {
                              setDeleteId(_id);
                              setDeleteModalVisibility(true);
                            }}
                              onArchived={() => {
                              setArchivedId(_id);
                              setArchivedModalVisibility(true);
                            }}
                              onDelivered={() => {
                              setDeliveredModalVisibility(true);
                              setEditItem(data[index]);
                            }}
                              onCorrection={() => {
                              setCorrectionModalVisibility(true);
                              setEditItem(data[index]);
                            }}
                            />
                          </TableData>
                        </PermissionsGate>
                        {
                        orderedPartsTableSetting.includes('ordered_part_id')
                        && <TableData className={tableCellClassName}>{id || ''}</TableData>
                      }
                        {
                        orderedPartsTableSetting.includes('ordered_type')
                        && (
                          <TableData className={tableCellClassName}>
                            {orderedType
                              && (
                                <Badge
                                  text={orderedType}
                                  type={ButtonTypes.LIGHT}
                                />
                              )}
                          </TableData>
                        )
                      }
                        {
                        orderedPartsTableSetting.includes('ordered_part_status')
                        && (
                          <TableData className={tableCellClassName}>
                            {partStatus
                              && (
                                <Badge
                                  text={partStatus}
                                  type={orderStatusInfo?.[partStatus]?.state}
                                />
                              )}
                          </TableData>
                        )
                      }
                        {
                        orderedPartsTableSetting.includes('ordered_part_order_date')
                        && (
                          <TableData className={tableCellClassName}>
                            <Badge
                              type={ButtonTypes.LIGHT}
                              text={DateTime.fromFormat(orderDate, 'yyyy-MM-dd HH:mm:ss', { zone: 'utc' })
                                .setLocale()
                                .toRelative()}
                            />
                          </TableData>
                        )
                      }
                        {
                        orderedPartsTableSetting.includes('ordered_part_po')
                        && (
                          <TableData className={tableCellClassName}>
                            <Text text={po || ''} bold />
                          </TableData>
                        )
                      }
                        {
                        orderedPartsTableSetting.includes('ordered_part_vendor')
                        && (
                          <TableData className={tableCellClassName}>
                            {vendor
                              && (
                                <Badge
                                  text={vendor}
                                  type="lightPrimary"
                                />
                              )}
                          </TableData>
                        )
                      }
                        {
                        orderedPartsTableSetting.includes('ordered_part_mpn')
                        && (
                          <TableData className={tableCellClassName}>
                            <div className="flex justify-center items-center">
                              <a
                                href={`/coilovers/warehouse/?query=${mpn}`}
                                className="globalLink"
                                target="_blank"
                                rel="noreferrer"
                              >
                                {mpn}
                              </a>
                            </div>
                          </TableData>
                        )
                      }
                        {
                        orderedPartsTableSetting.includes('ordered_part_qty_delivered')
                        && (
                          <TableData className={tableCellClassName}>
                            {qtyDelivered}
                          </TableData>
                        )
                      }
                        {
                        orderedPartsTableSetting.includes('ordered_part_qty_in_order')
                        && (
                          <TableData className={tableCellClassName}>
                            {qtyInOrder}
                          </TableData>
                        )
                      }
                        {
                        orderedPartsTableSetting.includes('ordered_part_qty_return')
                        && (
                          <TableData className={tableCellClassName}>
                            {qtyReturn}
                          </TableData>
                        )
                      }
                        {
                        orderedPartsTableSetting.includes('ordered_part_tracking')
                        && (
                          <TableData>
                            {trackingArr[0] && (
                              <a
                                href={prepareProviderLink(trackingArr[0])}
                                className="globalLink"
                                target="_blank"
                                rel="noreferrer"
                              >
                                {trackingArr[0]}
                              </a>
                            )}
                            {trackingArr.length > 1 && (
                              <button
                                type="button"
                                className={trackingMoreButtonClassName}
                                title="Expand row to see all tracking numbers"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  expandOrderedPartDetails(_id);
                                }}
                              >
                                +
                                {trackingArr.length - 1}
                                {' '}
                                more
                              </button>
                            )}
                          </TableData>
                        )
                      }
                        {
                        orderedPartsTableSetting.includes('ordered_part_note')
                        && (
                          <TableData className={tableCellClassName}>
                            <div className={noteClassName} title={note || undefined}>
                              {note}
                            </div>
                          </TableData>
                        )
                      }
                        {
                        orderedPartsTableSetting.includes('ordered_part_price_paid')
                        && (
                          <TableData className={tableCellClassName}>
                            {paid ? `$${paid}` : (
                              <div
                                className="cursor-pointer"
                                role="presentation"
                                onClick={() => {
                                  setEditItem(data[index]);
                                  setEditModalVisibility(true);
                                }}
                              >
                                <IconAlertCircle />
                              </div>
                            )}
                          </TableData>
                        )
                      }
                        {
                        orderedPartsTableSetting.includes('ordered_part_price_in_order')
                        && (
                          <TableData className={tableCellClassName}>
                            {priceInOrder ? `$${priceInOrder}` : (
                              <div
                                className="cursor-pointer"
                                role="presentation"
                                onClick={() => {
                                  setEditItem(data[index]);
                                  setEditModalVisibility(true);
                                }}
                              >
                                <IconAlertCircle />
                              </div>
                            )}
                          </TableData>
                        )
                      }
                        {
                        orderedPartsTableSetting.includes('ordered_part_est_delivery_date')
                        && (
                          <TableData className={tableCellClassName}>
                            {estDate && (
                              <Badge
                                type={ButtonTypes.LIGHT}
                                text={DateTime.fromFormat(estDate, 'yyyy-MM-dd', { zone: 'utc' })
                                  .setLocale().toFormat('DD')}
                              />
                            )}
                          </TableData>
                        )
                      }
                        {
                        orderedPartsTableSetting.includes('ordered_part_delivered_date')
                        && (
                          <TableData className={tableCellClassName}>
                            {deliveredDate && (
                              <Badge
                                type={ButtonTypes.LIGHT}
                                text={DateTime.fromFormat(deliveredDate, 'yyyy-MM-dd HH:mm:ss', { zone: 'utc' })
                                  .setLocale().toFormat('DD')}
                              />
                            )}
                          </TableData>
                        )
                      }
                        {
                        orderedPartsTableSetting.includes('ordered_part_type')
                        && (
                          <TableData className={tableCellClassName}>
                            {partType}
                          </TableData>
                        )
                      }
                        {
                        orderedPartsTableSetting.includes('ordered_part_brand')
                        && (
                          <TableData className={tableCellClassName}>
                            {brand}
                          </TableData>
                        )
                      }
                        {
                        orderedPartsTableSetting.includes('ordered_part_included')
                        && (
                          <TableData className={tableCellClassName}>
                            {include}
                          </TableData>
                        )
                      }
                        {
                        orderedPartsTableSetting.includes('ordered_part_agent')
                        && (
                          <TableData className={tableCellClassName}>
                            {agent}
                          </TableData>
                        )
                      }
                      </TableRow>
                      {detailsExpanded && <OrderedPartDetailsRow orderedPart={orderedPart} />}
                    </Fragment>
                  );
                })
                  : (
                    <EmptyTable colSpan={coOrderedPartsTable.length + 2} text="No any data find" />
                  )}
              </TableBody>
            </Table>
          </div>
        </Card>
        {editItem && (
          <>
            <OrderedPartsEditModal
              visible={editModalVisibility}
              toggleVisible={() => setEditModalVisibility(false)}
              item={editItem}
            />
            <DeliveredOrderedPartsModal
              visible={deliveredModalVisibility}
              toggleVisible={() => setDeliveredModalVisibility(false)}
              item={editItem}
            />
            <CorrectionOrderedPartsModal
              visible={correctionModalVisibility}
              toggleVisible={() => setCorrectionModalVisibility(false)}
              item={editItem}
            />
          </>
        )}
        {deleteId && (
          <ConfirmModal
            visible={deleteModalVisibility}
            toggleVisible={() => setDeleteModalVisibility(false)}
            confirmButtonText="Delete"
            confirmText="Part will be deleted permanently"
            loading={deleteLoading}
            onConfirm={() => dispatch(deleteOrderedParts(deleteId))}
            onCancel={() => {
              setDeleteId();
              dispatch(clearDeleteOrderedParts());
            }}
          />
        )}
        {archivedId && (
          <ConfirmModal
            visible={archivedModalVisibility}
            toggleVisible={() => setArchivedModalVisibility(false)}
            confirmButtonText="Archive"
            confirmText="Part will be send to archive"
            onConfirm={() => dispatch(patchOrderedParts({
              _id: archivedId,
              formData: {
                ordered_part_status: orderedPartsStatus.ARCHIVED
              }
            }))}
            onCancel={() => {
              setArchivedId();
              dispatch(clearPatchOrderedParts());
            }}
          />
        )}
      </PermissionsGate>
    </Layout>
  );
};

export default OrderedParts;
